/**
 * What a brand colour does to the trainer's own app: the accent family re-derived for each web
 * theme, as the custom properties `shared/design-system/theme/themeOverrides` lays over `<html>`.
 *
 * Ported from the mobile app's `resolveBrandTokens` (`src/design-system/brandColor.ts`). What is
 * ported is the RELATIONSHIPS, not the phone's token names: the soft wash is the accent at the
 * alpha Flame itself uses in that theme, the edge likewise, the words are the accent walked along
 * its own lightness until they clear AA against the surfaces the ember's hand-tuned inks were
 * measured on, and a fill that carries a white label is moved until the label reads.
 *
 * ── A PRESET is canon, like Flame; a CUSTOM colour is moved under its label ──────────────────
 * A preset's gradient, solid and stops are the handoff's, verbatim, exactly as Flame's are: no
 * stop is moved. Its label is the ink the preset table authored for it (`onTile`), white on five of
 * the six and `--ink-900` on Lime, which is the only case that writes `--on-accent` and
 * `--on-accent-grad`. Flame ships white on #ff8a45 and the owner accepts the canon; the presets get
 * the same treatment (owner, 23 September 2026). Moving a preset's stops would flatten its gradient.
 *
 * A custom colour is one flat hex with no authored ink, so its fill is still moved by
 * `readableFill` until white clears 4.5:1 on it, the phone's `accentCta`. Words in the accent
 * (`--accent-text`, `--accent-ink`) clear 4.5:1 on their surfaces for both kinds.
 *
 * ── The glow follows the brand ───────────────────────────────────────────────────────────────
 * The ember primary and the save button carry the canon's ember-hued cast (the owner's 2 September
 * exception, confirmed on 23 September 2026 with the ask that it follow the trainer's colour).
 * Flame's cast is `rgba(196,58,10,…)`, a deeper shade of the ember `#ff5a1f`; a brand's is the same
 * shade of its own solid (`shadeLike`), at the same geometry and alphas.
 *
 * ── What is deliberately NOT here ────────────────────────────────────────────────────────────
 * · `--pop`, `--pop-soft`, `--vol-over`, `--danger`, the result colours and every other state
 *   token, including where they share the ember's hex (owner, 23 September 2026). The `--ember*`
 *   primitives are not overridden either: Flame's `--pop` is `var(--ember)`, so moving the
 *   primitive would move a state.
 * · Anything at all for the default. Flame, or no row, is `null`: no inline property is written and
 *   the app is byte-identical to the shipped KANON port.
 *
 * ── A change to the OUTPUT bumps `BRAND_PAINT_HINT_VERSION` ───────────────────────────────────
 * The pre-paint hint stores this module's output precomputed and `public/theme-init.js` paints it
 * verbatim. Any edit that changes what `brandThemeOverrides` returns for some colour (a surface, an
 * alpha, a token added or removed, a new rule) bumps that constant in `brandPaintHint.ts` and in the
 * script, or the first load after the deploy pre-paints the old values and the shell repaints them.
 * `brandPaintHint.test.ts` writes through the module and runs the script, so a bump on one side
 * only fails there.
 */

import type { ThemeOverrides } from '~/shared/design-system/theme';
import {
  AA_TEXT_RATIO,
  compositeOver,
  readableFill,
  readableInk,
  shadeLike,
} from '~/shared/lib/contrast';
import { parseHex } from '~/shared/lib/hex';

import {
  DEFAULT_BRAND_COLOR,
  brandGradientCss,
  brandHues,
  isDefaultBrandColor,
  type BrandColor,
  type BrandHues,
} from './brandColor';

type BrandTheme = keyof ThemeOverrides;

/** A preset whose authored label is dark (Lime) writes this, the ink ramp's own near-black. */
const DARK_LABEL = 'var(--ink-900)';

/** The ink `--accent-cta` is measured against — `--on-cta` in `tokens/colors.css`, white always. */
const CTA_INK = '#ffffff';

/** The deeper shade Flame's cast shadows are drawn in, against the Flame preset's solid ember. */
const EMBER_CAST = '#c43a0a';
const EMBER = brandHues(DEFAULT_BRAND_COLOR).solid;

/**
 * The surfaces accent WORDS stand on, per theme: `--bg`, `--bg-card` and `--bg-inset`, each also
 * under the soft wash. `--accent-text`'s note names the canvas and `--accent-ink`'s names the wash
 * over the canvas and over a card; the inset is the phone's third surface for the same inks.
 * `brandTokens.test.ts` reads these three off `tokens/colors.css` rather than trusting this table.
 */
const WORD_SURFACES: Record<BrandTheme, readonly string[]> = {
  light: ['#eeece7', '#ffffff', '#faf9f7'],
  dark: ['#0b0b0c', '#161618', '#1a1a1d'],
};

/** Flame's own alphas, per theme — `--accent-soft`, `--accent-edge`, `--superset-soft`. */
const SOFT_ALPHA: Record<BrandTheme, number> = { light: 0.1, dark: 0.12 };
const EDGE_ALPHA: Record<BrandTheme, number> = { light: 0.28, dark: 0.38 };
const SUPERSET_SOFT_ALPHA: Record<BrandTheme, number> = { light: 0.09, dark: 0.14 };
/** `--superset-ring`, theme-independent in Flame and here. */
const SUPERSET_RING_ALPHA = 0.55;

/** A light theme darkens an ink to make it read, a dark theme brightens it. */
const INK_DIRECTION = { light: 'darken', dark: 'lighten' } as const;

function rgba(hex: string, alpha: number): string {
  const { r, g, b } = parseHex(hex);
  return `rgba(${String(r)},${String(g)},${String(b)},${String(alpha)})`;
}

/** What one theme's half is built from; the theme-independent part is computed once. */
interface BrandBasis {
  readonly hues: BrandHues;
  /** The deeper shade of the solid the casts are drawn in. */
  readonly cast: string;
}

/** The custom properties one non-default brand writes for one theme. */
function brandCssVars(basis: BrandBasis, theme: BrandTheme): Readonly<Record<string, string>> {
  const { hues, cast } = basis;
  const accent = hues.fill;
  const [from, mid, to] = hues.stops;

  const surfaces = WORD_SURFACES[theme].flatMap((surface) => [
    surface,
    compositeOver(accent, SOFT_ALPHA[theme], surface),
  ]);
  const words = readableInk(hues.solid, surfaces, AA_TEXT_RATIO, INK_DIRECTION[theme]);

  return {
    '--accent': accent,
    '--accent-2': to,
    // Top to bottom, as Flame's own `--ember-grad` runs, and laid out from the border box as that
    // token is; the rule is the same stops turned 90deg.
    '--accent-grad': `${brandGradientCss(hues.stops, hues.locations)} border-box`,
    '--accent-grad-from': from,
    '--accent-grad-mid': mid,
    '--accent-grad-to': to,
    '--accent-grad-rule': brandGradientCss(hues.stops, hues.locations, '90deg'),
    '--accent-soft': rgba(accent, SOFT_ALPHA[theme]),
    '--accent-edge': rgba(accent, EDGE_ALPHA[theme]),
    // Both are held to 4.5:1 under a brand (owner, 23 September 2026), so they are one value. In
    // Flame they differ because `--accent-text` is a hand-tuned MARK at 3.86:1.
    '--accent-text': words,
    '--accent-ink': words,
    // A FILL that carries white words — the phone's `accentCta`: the brand's solid moved until
    // white text reads on it at AA. White in both themes, so one value; the booking grid's
    // training block paints with it, exactly as mobile's `resolveBlockFill` does.
    '--accent-cta': readableFill(hues.solid, CTA_INK, AA_TEXT_RATIO),
    // The superset is drawn in the accent by the handoff (see `--superset` in Flame Dark), and the
    // phone re-derives it from the brand the same way: label in the words ink, ring in the accent.
    '--superset': words,
    '--superset-ring': rgba(accent, SUPERSET_RING_ALPHA),
    '--superset-soft': rgba(words, SUPERSET_SOFT_ALPHA[theme]),
    // Flame's two casts, geometry and alphas verbatim, in the brand's shade (`tokens/colors.css`).
    '--shadow-cta-ember': `0 1px 2px ${rgba(cast, 0.26)}, 0 5px 12px -4px ${rgba(cast, 0.3)}`,
    '--shadow-save': `inset 0 1px 0 rgba(255,255,255,0.3), 0 5px 12px -4px ${rgba(cast, 0.3)}`,
    ...(hues.label === 'dark' ? { '--on-accent': DARK_LABEL, '--on-accent-grad': DARK_LABEL } : {}),
  };
}

/**
 * Both themes' overrides for a choice, or `null` for the default — the one entry point. The pair is
 * computed together because the pre-paint hint stores it together: the boot script does no colour
 * maths, it only picks the half its theme class names.
 */
export function brandThemeOverrides(brand: BrandColor): ThemeOverrides | null {
  if (isDefaultBrandColor(brand)) return null;
  const hues = brandHues(brand);
  const basis: BrandBasis = { hues, cast: shadeLike(hues.solid, EMBER, EMBER_CAST) };
  return { light: brandCssVars(basis, 'light'), dark: brandCssVars(basis, 'dark') };
}
